<script>
import Price from "../Price.vue";
import CartAPI from "$vueCommon/classes/API/CartAPI.js";
import CheckoutAPI from "$vueCommon/classes/API/CheckoutAPI.js";
import {ToastMessageDanger, ToastMessageSuccess, ToastMessageWarning} from "$vueCommon/classes/ToastMessage.js";

export default {
    components: {Price},
    emits: ['PayNow'],
    props: {
        cartSummary: null,
        discountCode: null,
        source: String,
        placingOrder: null,
        manager: Boolean,
        fee: Object
    },
    mounted() {
        this.watchScrollChanges();

        if (typeof (cartUrl) !== 'undefined') {
            this.cartUrl = cartUrl;
        }

        if (typeof (checkoutUrl) !== 'undefined') {
            this.checkoutUrl = checkoutUrl;
        }
    },
    updated() {
        this.watchScrollChanges();
    },
    data() {
        return {
            displayPromoForm: false,
            localPromoCode: '',
            isDiscountCodeInvalid: true,
            displayStickyButton: false,
            discountCodeError: null,
            cartUrl: '',
            checkoutUrl: ''
        }
    },
    methods: {
        applyPromoCode() {
            this.discountCodeError = null;

            if (this.source === 'Cart') {
                CartAPI.postDiscountCode(this.localPromoCode).then(response => {
                    this.displayPromoForm = false;
                }, responseError => {
                    this.discountCodeError = responseError.response.data.message;
                });
            } else {
                CheckoutAPI.postDiscountCode(this.localPromoCode).then(response => {
                    this.displayPromoForm = false;
                }, responseError => {
                    this.discountCodeError = responseError.response.data.message;
                });
            }
        },
        deletePromoCode() {
            this.localPromoCode = '';
            this.applyPromoCode();
        },
        payNow() {
            this.$emit('PayNow');
        },
        scrollEventListener(e) {
            const buttonElement = document.getElementById('summary-button-pay');
            if (!buttonElement) {
                return;
            }

            this.displayStickyButton = buttonElement.getBoundingClientRect().y > window.innerHeight;
        },
        watchScrollChanges() {
            window.removeEventListener('scroll', this.scrollEventListener);
            window.addEventListener('scroll', this.scrollEventListener);
        }
    },
    computed: {
        subtotal() {
            return this.cartSummary.subtotal - (this.fee ? this.fee.subtotal : 0);
        },
        promoSubtotal() {
            return this.cartSummary.promo_subtotal - (this.fee ? this.fee.subtotal : 0);
        },
        total() {
            return this.cartSummary.total;
        },
        promoTotal() {
            return this.cartSummary.promo_total;
        },
        discountSubtotal() {
            return Math.round((this.subtotal - this.promoSubtotal) * 100) / 100;
        },
        discountTax() {
            return Math.round((this.cartSummary.tax - this.cartSummary.promo_tax) * 100) / 100;
        },
        discountTotal() {
            return Math.round((this.total - this.promoTotal) * 100) / 100;
        },
    }
}
</script>
<template>
    <div class="component-summary mb-3" :class="{'display-sticky-button': displayStickyButton}">
        <h2 class="h4">{{ $translate('checkout.summary') }}</h2>
        <div class="card">
            <div class="card-body">
                <div class="summary-promo">
                    <div>
                        <button v-if="!this.discountCode" @click="displayPromoForm = true; localPromoCode = ''"
                                class="btn-link">{{ $translate('checkout.add_discount_code') }}
                        </button>
                        <template v-if="this.discountCode">
                            <div class="d-flex justify-content-between align-items-center">
                                <div><strong>{{ discountCode.code }}</strong></div>
                                <button v-if="this.discountCode" @click="deletePromoCode"
                                        class="btn btn-secondary btn-sm">{{ $translate('checkout.delete_discount_code')
                                    }}
                                </button>
                            </div>
                        </template>
                    </div>
                    <div v-if="displayPromoForm" class="summary-promo-form">
                        <div>
                            <input type="text" class="form-control" v-model="localPromoCode"
                                   :placeholder="$translate('checkout.discount_code')">
                        </div>
                        <div>
                            <button @click="applyPromoCode" :class="{disabled: !this.localPromoCode}"
                                    class="btn btn-secondary btn-md">{{ $translate('checkout.apply') }}
                            </button>
                        </div>
                        <div>
                            <button @click="displayPromoForm = false; discountCodeError = null" class="btn btn-white btn-md">x</button>
                        </div>
                    </div>
                    <div v-if="displayPromoForm" class="mt-3 text-center">
                        <div class="alert alert-danger alert-sm" v-if="discountCodeError">{{ discountCodeError }}</div>
                        <div class="small">
                            <small v-html="$translate('checkout.discount_code_error_marketing_text', {url: '/domain-discounts'})"></small>
                        </div>
                    </div>
                </div>
                <hr>
                <div v-if="!cartSummary">
                    <div class="vue-loader-dark"></div>
                </div>
                <div class="prices" v-if="cartSummary">
                    <div>
                        <div><strong>{{ $translate('checkout.subtotal') }}</strong></div>
                        <div><strong>
                            <Price :price="subtotal"></Price>
                        </strong></div>
                    </div>
                    <div v-if="fee">
                        <div>{{ $translate('checkout.transaction_fee') }} (PayPal)</div>
                        <div>
                            <Price :price="fee.subtotal"></Price>
                        </div>
                    </div>
                    <div v-if="discountSubtotal">
                        <div>{{ $translate('manager-orders.discount') }}</div>
                        <div>-
                            <Price :price="discountSubtotal"></Price>
                        </div>
                    </div>
                    <div>
                        <div>{{ $translate('checkout.vat') }}</div>
                        <div>
                            <Price :price="cartSummary.promo_tax"></Price>
                        </div>
                    </div>
                    <div class="h5">
                        <div>{{ $translate('checkout.total') }}</div>
                        <Price :price="promoTotal"></Price>
                    </div>
                </div>
                <button v-if="source === 'Checkout'" id="summary-button-pay" class="btn btn-warning w-100 mt-3"
                        @click="payNow" :class="{'disabled': placingOrder}">
                    <template v-if="!placingOrder">{{ $translate('checkout.order_and_pay') }}</template>
                    <template v-if="placingOrder">
                        <div class="vue-loader"></div>
                    </template>
                </button>
                <a v-if="source === 'Cart'" class="btn btn-warning w-100 mt-3" id="summary-button-pay"
                   :href="checkoutUrl">{{ $translate('checkout.go_to_checkout') }} <i
                    class="fas fa-angle-double-right ms-2"></i></a>
            </div>
        </div>
        <div class="sticky-button d-md-none">
            <button v-if="source === 'Checkout'" id="summary-button-sticky" class="btn btn-warning w-100 mt-3"
                    @click="payNow" :class="{'disabled': placingOrder}">
                <template v-if="!placingOrder">{{ $translate('checkout.order_and_pay') }}</template>
                <template v-if="placingOrder">
                    <div class="vue-loader"></div>
                </template>
            </button>
            <div v-if="source === 'Cart'" id="summary-button-sticky">
                <a v-if="source === 'Cart'" class="btn btn-warning w-100 mt-3"
                   :href="checkoutUrl">{{ $translate('checkout.go_to_checkout') }} <i
                    class="fas fa-angle-double-right ms-2"></i></a>
            </div>
        </div>
    </div>
</template>
